/* @jsx h */
import { defineComponent, h, onMounted, ref } from "vue";
import { Payment, PaymentDetails } from "@lemmonhq/types";
import { formatPriceInLocale, getOrderDisplayName } from "@lemmonhq/orders";
import { RightSheet, useLocalization } from "@lemmonhq/uikit";
import { getStatusClass } from "./helpers";
import { api } from "@app/data/api";

import { LemmonModal, Loading } from "@app/vue/components/UIComponents";
import { useAuth } from "@app/vue/providers/Auth";

interface TransactionDetailsProps {
  payment: Payment;
  onPrintReceipt: (paymentId: number) => void;
  onShowDigitalReceipt: (payment: Payment) => void;
  onShowEditPayment: (payment: Payment) => void;
  onShowVoidPayment?: (payment: Payment) => void;
  onShowReopenTable?: (payment: Payment) => void;
  onShowCreateCreditNode: (paymentId: number) => void;
  onClose: () => void;
  showDetailsInModal?: boolean;
}

// unsued for now
export const TransactionDetails = defineComponent<TransactionDetailsProps>({
  name: "TransactionItem",
  props: [
    "payment",
    "onPrintReceipt",
    "onShowDigitalReceipt",
    "onShowCreateCreditNode",
    "onShowEditPayment",
    "onShowVoidPayment",
    "onShowReopenTable",
    "onClose",
    "showDetailsInModal",
  ],
  setup(props) {
    const auth = useAuth();
    const { t } = useLocalization();
    const orders = (
      !props.payment.order && !props.payment.root_table_orders
        ? []
        : props.payment.root_table_orders || [props.payment.order]
    ).filter((el) => el);
    const loaded = ref<false | Error | PaymentDetails>(false);

    onMounted(async () => {
      try {
        const paymentDetails = await api.getPayment(props.payment.id);
        loaded.value = paymentDetails;
      } catch (e: any) {
        loaded.value = e;
      }
    });
    return () => {
      let content = (() => {
        if (loaded.value == false) {
          return (
            <div class="flex justify-center items-center min-h-64">
              <Loading />
            </div>
          );
        } else if (loaded.value && loaded.value instanceof Error) {
          return (
            <div class="flex justify-center items-center min-h-64">
              <span>Error</span>
            </div>
          );
        } else {
          const payment = loaded.value;
          return (
            <>
              <div class="p-2 lg:p-4 space-y-6">
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <div class="text-sm ">Amount</div>
                    <div class="text-accent font-mono">
                      {formatPriceInLocale(
                        payment.amount + (payment.tips || 0),
                        true,
                        2,
                        auth.user.value?.restaurant.currency || "CHF",
                      )}
                    </div>
                  </div>
                  <div>
                    <div class="text-sm ">Payment Method</div>
                    <div class="text-accent font-mono capitalize">
                      {payment.method || "-"}
                    </div>
                  </div>
                  {payment.type === "sale" ? (
                    <div>
                      <div class="text-sm ">Orders</div>
                      <div class="text-accent font-mono capitalize">
                        {orders.map((o) => getOrderDisplayName(o)).join(", ")}
                      </div>
                    </div>
                  ) : null}
                  <div>
                    <div class="text-sm ">Service Type</div>
                    <div class="text-accent font-mono capitalize">
                      {payment.type === "sale"
                        ? Array.from(
                            new Set(orders.map((el) => el.service_method)),
                          ).join(", ")
                        : "REFUND"}
                    </div>
                  </div>
                  <div>
                    <div class="text-sm ">Status</div>
                    <div
                      class={`text-accent font-mono uppercase ${getStatusClass(
                        payment.status,
                      )}`}
                    >
                      {payment.status}
                    </div>
                  </div>
                  <div>
                    <div class="text-sm ">Time</div>
                    <div class="text-accent font-mono">
                      {new Date(payment.created_at).toLocaleString()}
                    </div>
                  </div>
                  {payment.tips ? (
                    <div>
                      <div class="text-sm ">Tips</div>
                      <div class="text-accent font-mono">
                        {formatPriceInLocale(
                          payment.tips,
                          true,
                          2,
                          auth.user.value?.restaurant.currency || "CHF",
                        )}
                      </div>
                    </div>
                  ) : null}
                  <div>
                    <div class="text-sm ">ID</div>
                    <div class="text-accent font-mono">{payment.guid}</div>
                  </div>
                  {payment.transaction ? (
                    <>
                      <div>
                        <div class="text-sm ">Provider</div>
                        <div class="text-accent font-mono uppercase">
                          {payment.transaction.provider}
                        </div>
                      </div>
                      <div>
                        <div class="text-sm ">Provider Id</div>
                        <div class="text-accent font-mono">
                          {payment.transaction.providerId}
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>

                {payment.type === "sale" && (
                  <div class=" flex gap-1 md:gap-2 justify-end p-1 md:p-4 rounded-lg border  justify-between lg:justify-end ">
                    <button
                      onClick={() => {
                        props.onPrintReceipt(payment.id);
                      }}
                      class="btn btn-primary flex-1 lg:flex-none"
                    >
                      <span class="material-symbols-outlined mr-0">print</span>
                      <span class="hidden sm:inline">
                        {t("general.words.print")}
                      </span>
                    </button>
                    <button
                      class="btn btn-neutral flex-1 lg:flex-none"
                      onClick={() => {
                        props.onShowDigitalReceipt(payment);
                      }}
                    >
                      <span class="material-symbols-outlined mr-0">
                        forward_to_inbox
                      </span>
                      <span class="hidden sm:inline">
                        {t("reports.tx.digital_receipt.send_action")}
                      </span>
                    </button>
                    <button
                      class="btn btn-neutral flex-1 lg:flex-none"
                      onClick={() => {
                        props.onShowCreateCreditNode(payment.id);
                      }}
                    >
                      <span class="material-symbols-outlined mr-0">undo</span>
                      <span class="hidden sm:inline">
                        {t("reports.tx.nav.refund")}
                      </span>
                    </button>
                    <button
                      class="btn btn-neutral flex-1 lg:flex-none"
                      onClick={() => {
                        props.onShowEditPayment(payment);
                      }}
                    >
                      <span class="material-symbols-outlined mr-0">
                        edit_square
                      </span>
                      <span class="hidden sm:inline">
                        {t("general.words.edit")}
                      </span>
                    </button>
                    {props.onShowVoidPayment && payment.status !== "voided" ? (
                      <button
                        class="btn btn-error flex-1 lg:flex-none"
                        onClick={() => {
                          props.onShowVoidPayment?.(payment);
                        }}
                      >
                        <span class="material-symbols-outlined mr-0">
                          block
                        </span>
                        <span class="hidden sm:inline">
                          {t("reports.tx.void_payment.action")}
                        </span>
                      </button>
                    ) : null}
                    {props.onShowReopenTable &&
                    payment.table_id &&
                    payment.status !== "voided" ? (
                      <button
                        class="btn btn-warning flex-1 lg:flex-none"
                        onClick={() => {
                          props.onShowReopenTable?.(payment);
                        }}
                      >
                        <span class="material-symbols-outlined mr-0">
                          table_restaurant
                        </span>
                        <span class="hidden sm:inline">
                          {t("reports.tx.list.reopen_table")}
                        </span>
                      </button>
                    ) : null}
                  </div>
                )}

                {/* A credit note only offers the void action — everything else
                    on this bar acts on the sale it was issued against. */}
                {payment.type === "credit" &&
                props.onShowVoidPayment &&
                payment.status !== "voided" ? (
                  <div class="flex gap-1 md:gap-2 p-1 md:p-4 rounded-lg border justify-between lg:justify-end">
                    <button
                      class="btn btn-error flex-1 lg:flex-none"
                      onClick={() => {
                        props.onShowVoidPayment?.(payment);
                      }}
                    >
                      <span class="material-symbols-outlined mr-0">block</span>
                      <span class="hidden sm:inline">
                        {t("reports.tx.void_payment.action_credit")}
                      </span>
                    </button>
                  </div>
                ) : null}
              </div>
            </>
          );
        }
      })();
      if (props.showDetailsInModal) {
        return (
          <LemmonModal
            onClose={() => props.onClose()}
            title={`Transaction ${props.payment.id}`}
          >
            {content}
          </LemmonModal>
        );
      } else {
        return (
          <RightSheet
            isOpen={true}
            onClose={() => props.onClose()}
            title={
              <span class="text-accent text-2xl">
                Transaction {props.payment.id}
              </span>
            }
          >
            {content}
          </RightSheet>
        );
      }
    };
  },
});
